// src/pages/auth/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAuthAPI } from '../../services/api';
import AuthLeftPanel from './AuthLeftPanel';
import './AuthPages.css';

const ForgotPassword = () => {
  const navigate   = useNavigate();
  const [step, setStep]               = useState(1); // 1=email, 2=otp+password
  const [email, setEmail]             = useState('');
  const [otp, setOtp]                 = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await userAuthAPI.forgotPassword(email);
      setSuccess('OTP sent to your email. Valid for 15 minutes.');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm)           { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8)            { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await userAuthAPI.resetPassword(email, otp, newPassword);
      setSuccess('Password reset successfully! Redirecting...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <AuthLeftPanel />

      <div className="auth-right">
        <div className="auth-right-inner">

          {/* ── STEP 1: Enter Email ── */}
          {step === 1 && (
            <>
              <div className="auth-right-header">
                <div className="auth-otp-icon"><i className="bi bi-key-fill"></i></div>
                <h1 className="auth-right-title">Forgot Password</h1>
                <p className="auth-right-subtitle">
                  Enter your registered email and we'll send you an OTP to reset your password.
                </p>
              </div>

              {error   && <div className="auth-alert auth-alert-error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}
              {success && <div className="auth-alert auth-alert-success"><i className="bi bi-check-circle-fill"></i>{success}</div>}

              <form onSubmit={handleSendOtp} className="auth-form">
                <div className="auth-field">
                  <label className="auth-field-label">Email Address</label>
                  <div className="auth-input-wrap">
                    <input
                      type="email"
                      className="auth-input"
                      placeholder="Enter your registered email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      required disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading
                    ? <><span className="auth-spinner"></span> Sending OTP...</>
                    : <><span>Send OTP</span><i className="bi bi-send-fill"></i></>
                  }
                </button>
              </form>

              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button className="auth-back-link" onClick={() => navigate('/login')}>
                  <i className="bi bi-arrow-left"></i> Back to Login
                </button>
              </div>
            </>
          )}

          {/* ── STEP 2: OTP + New Password ── */}
          {step === 2 && (
            <>
              <div className="auth-right-header">
                <div className="auth-otp-icon"><i className="bi bi-envelope-check-fill"></i></div>
                <h1 className="auth-right-title">Reset Password</h1>
                <p className="auth-right-subtitle">
                  OTP sent to <strong>{email}</strong>.<br/>Enter it below with your new password.
                </p>
              </div>

              {error   && <div className="auth-alert auth-alert-error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}
              {success && <div className="auth-alert auth-alert-success"><i className="bi bi-check-circle-fill"></i>{success}</div>}

              <form onSubmit={handleReset} className="auth-form">
                <div className="auth-field">
                  <label className="auth-field-label">OTP Code</label>
                  <div className="auth-input-wrap">
                    <input
                      type="text"
                      className="auth-input auth-otp-input"
                      placeholder="• • • • • •"
                      value={otp}
                      onChange={(e) => { setOtp(e.target.value.replace(/\D/,'').slice(0,6)); setError(''); }}
                      required disabled={loading}
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-field-label">New Password</label>
                  <div className="auth-input-wrap">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="auth-input has-icon-right"
                      placeholder="Min 8 characters"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      required disabled={loading}
                    />
                    <button type="button" className="auth-input-eye" onClick={() => setShowPwd(!showPwd)}>
                      <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-field-label">Confirm Password</label>
                  <div className="auth-input-wrap">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="auth-input"
                      placeholder="Re-enter new password"
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setError(''); }}
                      required disabled={loading}
                    />
                  </div>
                </div>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading
                    ? <><span className="auth-spinner"></span> Resetting...</>
                    : <><span>Reset Password</span><i className="bi bi-check-circle"></i></>
                  }
                </button>
              </form>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button className="auth-back-link" onClick={() => { setStep(1); setError(''); setSuccess(''); }}>
                  <i className="bi bi-arrow-left"></i> Use different email
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
